<template>
	<TeamListCardLayout
		:class="classes"
		@click="$emit( `navTo`, { id: team.id, } )"
	>
		<template v-slot:favorite>
			<ToggleStar
				:is-toggled="!!team.isFavorited"
				@change="onStarClicked"
			/>
		</template>
		<template v-slot:name>
			<div class="team-list-card__name-container--normal">
				<div class="team-list-card__name" v-tooltip.top="team.name">{{ team.name }}</div>
			</div>
			<div class="team-list-card__name-container--xs">
				<div class="team-list-card__fave-and-name-container">
					<ToggleStar
						:is-toggled="!!team.isFavorited"
						@change="onStarClicked"
					/>
					<div class="team-list-card__name" v-tooltip.top="team.name">{{ team.name }}</div>
				</div>
				<div class="team-list-card__mobile-avatar-list">
					<UserAvatarSpread
						:users="team.members"
						:overlap-mode="avatarOverlapMode"
						:maxAvatars="maxTeamAvatars"
						avatar-size="1.75rem"
					/>
				</div>
			</div>
		</template>
		<template v-slot:leads>
			<div class="team-list-card__lead-avatars-container">
				<UserAvatarSpread
					:users="team.leads"
					:overlap-mode="avatarOverlapMode"
					:maxAvatars="maxLeadAvatars"
					avatar-size="1.75rem"
				/>
				<div class="team-list-card__first-lead-name">{{ firstLeadName }}</div>
			</div>
		</template>
		<template v-slot:members>
			<UserAvatarSpread
				:users="team.members"
				:overlap-mode="avatarOverlapMode"
				avatar-size="1.75rem"
				:maxAvatars="maxTeamAvatars"
			/>
		</template>
		<template v-slot:chevron>
			<FontAwesomeIcon
				class="team-list-card__chevron"
				:icon="[ 'fas', 'chevron-right' ]"
			/>
		</template>
	</TeamListCardLayout>
</template>

<script>
import EnumsMixin from "../../mixins/EnumsMixin";
import TeamListCardLayout from "./TeamListCardLayout";
import ToggleStar from "../ToggleStar";
import UserAvatarSpread, { OVERLAP_MODE_TIGHT } from "../UserAvatarSpread.vue";

export default {
	name: "TeamListCard",
	mixins: [ EnumsMixin, ],
	emits: [
		"favorite",
		"unfavorite",
		"navTo",
	],
	components: {
		TeamListCardLayout,
		ToggleStar,
		UserAvatarSpread,
	},
	props: {
		className: {
			type: String,
			default: "",
		},
		team: {
			type: Object,
			required: true,
			validator( team ) {
				return ( team.id );
			},
		},
	},
	computed: {
		avatarOverlapMode() {
			return OVERLAP_MODE_TIGHT;
		},
		classes() {
			return {
				"team-list-card": true,
				"team-list-card--is-sample-team": !!this.team?.isTeamingTeam,
				[ this.className ]: !!this.className,
			};
		},
		firstLeadName() {
			const firstLead = this.team.leads.filter( x => x.name )[ 0 ] || {};
			return firstLead.displayName || "";
		},
		/* eslint-disable no-magic-numbers */
		maxLeadAvatars() {
			return 2;
		},
		maxTeamAvatars() {
			return 6;
		},
		/* eslint-enable no-magic-numbers */
	},
	methods: {
		onStarClicked( e ) {
			const eventName = e.isToggled ? "favorite" : "unfavorite";
			this.$emit( eventName, { id: this.team.id, } );
		},
	},
};
</script>

<style lang="scss" scoped>
.team-list-card {
	border-color: $team-list-card-border-color;
	box-shadow: 0 0.0625rem 0.25rem rgba(42, 47, 69, 0.1);
	cursor: pointer;
}

.team-list-card__name {
	display: inline-flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;

	font-weight: 600;
	color: $team-list-card-name-color;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;

	@include for-size('xs') {
		display: inline;
		max-width: 8.5rem;
		transform: translateY(0.28rem);
	}

	@include for-size('sm') {
		display: inline-block;
		transform: translateY(0.28rem);
		max-width: 13.5rem;
	}

	@include for-size('md') {
		display: inline-block;
		transform: translateY(0.28rem);
		max-width: 13.5rem;
	}

	@include for-size('lg') {
		display: inline-block;
		transform: translateY(0.28rem);
		max-width: 16rem;
	}

	@include for-size('xl') {
		display: inline-block;
		transform: translateY(0.28rem);
		max-width: 19.5rem;
	}
}

.team-list-card__name-container--normal {
	@include for-size-and-smaller('xs') {
		display: none;
	}
}

.team-list-card__name-container--xs {
	display: block;
	@include for-size-and-larger('sm') {
		display: none;
	}

	.team-list-card__name {
		margin-left: .5rem;
	}
}

.team-list-card__fave-and-name-container {
	text-align: left;

	@include for-size('xs') {
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
		max-width: 18rem;
	}
}

.team-list-card__mobile-avatar-list {
	margin-top: .75rem;
	.user-avatar-spread {
		align-items: center;
		display: flex;
	}

	.user-avatar-spread__avatar {
		margin-right: .25rem;
	}
}

.team-list-card__lead-avatars-container {
	display: flex;
	align-items: center;
}

.team-list-card__first-lead-name {
	margin-left: .5rem;
	@include font-and-line-for(0);
	color: $team-list-card-lead-font-color;
}

.team-list-card__chevron {
	color: $team-list-card-chevron-color;
}
</style>
